import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from "@mui/material/IconButton";

const Wrapper = styled.div`
    position: relative;
    z-index: 2;
    align-items: center;
    justify-content: center;
    width: 30%;
    padding: 10px;
    display: flex;
    top: -15px;
`;

const SuggestionsList = styled.ul`
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    list-style: none;
    margin: 4px 0 0 0;
    padding: 0;
    max-height: 250px;
    overflow-y: auto;
    z-index: 3;
`;

const SuggestionItem = styled.li`
    padding: 10px 12px;
    cursor: pointer;
    font-size: 14px;

    &:hover {
        background: #f2f2f2;
    }
`;

const AutoComplete = ({ map, mapApi, addplace }) => {
    const [inputValue, setInputValue] = useState('');
    const [predictions, setPredictions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);

    const searchInputRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const autocompleteServiceRef = useRef(null);
    const placesServiceRef = useRef(null);

    // Init Google services once
    useEffect(() => {
        autocompleteServiceRef.current = new mapApi.places.AutocompleteService();
        placesServiceRef.current = new mapApi.places.PlacesService(document.createElement('div'));

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [mapApi]);

    const fetchPredictions = useCallback((input) => {
        if (!input) {
            setPredictions([]);
            setShowDropdown(false);
            return;
        }

        autocompleteServiceRef.current.getPlacePredictions(
            { input },
            (results, status) => {
                if (status !== 'OK' || !results) {
                    setPredictions([]);
                    setShowDropdown(false);
                    return;
                }
                setPredictions(results);
                setShowDropdown(true);
            }
        );
    }, []);

   
    const debouncedFetchPredictions = useCallback((input) => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            fetchPredictions(input);
        }, 500);
    }, [fetchPredictions]);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        debouncedFetchPredictions(value);
    };

    const handleSelectPrediction = (prediction) => {
        placesServiceRef.current.getDetails(
            { placeId: prediction.place_id },
            (place, status) => {
                if (status !== 'OK' || !place || !place.geometry) {
                    console.warn('No geometry for selected place');
                    return;
                }

                if (place.geometry.viewport) {
                    map.fitBounds(place.geometry.viewport);
                } else {
                    map.setCenter(place.geometry.location);
                    map.setZoom(17);
                }

                addplace(place);

                setInputValue(prediction.description);
                setPredictions([]);
                setShowDropdown(false);
                searchInputRef.current?.blur();
            }
        );
    };

    const clearSearchBox = () => {
        setInputValue('');
        setPredictions([]);
        setShowDropdown(false);
    };

    return (
        <Wrapper>
            <input
                className="form-control"
                ref={searchInputRef}
                type="text"
                value={inputValue}
                onFocus={clearSearchBox}
                onChange={handleInputChange}
                placeholder="Enter a location"
                autoComplete="off"
            />
            <IconButton onClick={() => predictions[0] && handleSelectPrediction(predictions[0])}>
                <SearchIcon />
            </IconButton>

            {showDropdown && predictions.length > 0 && (
                <SuggestionsList>
                    {predictions.map((prediction) => (
                        <SuggestionItem
                            key={prediction.place_id}
                            onClick={() => handleSelectPrediction(prediction)}
                        >
                            {prediction.description}
                        </SuggestionItem>
                    ))}
                </SuggestionsList>
            )}
        </Wrapper>
    );
};

export default AutoComplete;